import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Header, Radio} from '@thryvlabs/maverick'
import {searchOptions, tierForQuantity} from '../../utils/lift-lead-options'
import {SearchDropdown} from '../catalog-components/lead-lift-dropdown'

const radiooptions = [
  {label: 'No', value: 'No', default: true},
  {label: 'Yes', value: 'Yes'},
]

// Parse amounts from strings like "Premium - $2,000"
const parseAmount = str => {
  if (!str) return undefined
  const n = Number(String(str).replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? n : undefined
}

function LeadsLiftV4({LLAmount, setLLAmount, accountInfo}) {
  const defaultOption = radiooptions.find(x => x.default)
  const [optionValue, setOptionValue] = useState(defaultOption.value)
  const [picked, setPicked] = useState(null)
  const [isLLOptionEnabled, setLLOptionEnabled] = useState(
    defaultOption.value === 'Yes',
  )
  const [minAmountFilter, setMinAmountFilter] = useState(null) // if seeded (e.g., 1500), only show > this
  const didInitFromAccount = useRef(false)

  const disableStyle = {pointerEvents: 'none', opacity: 0.5}

  const handleOnChange = e => {
    if (isLLOptionEnabled) return
    const val = e.target.value
    setOptionValue(val)
    setLLOptionEnabled(val === 'Yes')
  }

  // One-time init from accountInfo
  useEffect(() => {
    if (didInitFromAccount.current) return
    didInitFromAccount.current = true

    const leadLift = accountInfo?.addOns?.find(
      ({addOnCode, quantity}) =>
        addOnCode === 'addon-4-leadslift-m2m' && Number(quantity) > 0,
    )

    if (leadLift) {
      // leadLift.quantity is the Recurly accounting number, not a dollar
      // amount (e.g., AU $1500 tier stores quantity=1363). Reverse-map it
      // to the dollar tier so the dropdown and getAmountToQuantityMap work
      // correctly. Mirrors tierForQuantity in LeadsLiftPanel.js (5.0 flow).
      const country = accountInfo?.billingAddress?.country
      const amountNum = tierForQuantity(Number(leadLift.quantity), country)

      setOptionValue('Yes')
      setLLOptionEnabled(true)
      setLLAmount?.(amountNum)
      setMinAmountFilter(amountNum)

      // Show initial selection chip
      setPicked({
        label: `Selected - $${amountNum.toLocaleString()}`,
        value: `Selected - $${amountNum.toLocaleString()}`,
        amountNum,
        amountStr: `$${amountNum.toLocaleString()}`,
        tierLabel: 'Custom',
      })
    } else {
      setOptionValue('No')
      setLLOptionEnabled(false)
      setMinAmountFilter(null)
    }
  }, [accountInfo, setLLAmount])

  // Keep LLAmount synced with user selection
  useEffect(() => {
    if (picked && picked.amountNum != null && picked.amountNum !== 0) {
      setLLAmount?.(picked.amountNum)
    }
  }, [picked, setLLAmount])

  // Build dropdown options; annotate with Upgrade/Current/Downgrade tag when an existing tier is known
  const dropdownOptions = useMemo(() => {
    if (minAmountFilter == null) return searchOptions
    const min = Number(minAmountFilter)
    return (searchOptions ?? []).map(txt => {
      const amt = parseAmount(txt)
      let tag
      if (Number.isFinite(amt)) {
        if (amt > min) tag = 'Upgrade'
        else if (amt === min) tag = 'Current'
        else tag = 'Downgrade'
      }
      return tag ? {label: txt, value: txt, tag} : txt
    })
  }, [minAmountFilter])

  return (
    <div className="ml-3" style={{borderBottom: '1px solid #eee'}}>
      <div
        className="row ml-0"
        style={{marginTop: '1rem', alignItems: 'center'}}
      >
        <div className="col-lg-6">
          <div className="float-left d-flex" style={{position: 'relative'}}>
            <Header className="mr-2" fontWeight="normal" variant="h4">
              Leads Lift
              <p
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: 'rgb(163, 165, 167)',
                  fontStyle: 'normal',
                  fontFamily: 'open-sans',
                }}
              >
                {`$${LLAmount || 0}`}
              </p>
            </Header>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="float-right" style={{position: 'relative'}}>
            <div className="row">
              {radiooptions.map((option, key) => (
                <Radio
                  key={key}
                  className="ml-6"
                  hasLabel
                  id={`Leads Lift ${option.value}`}
                  name="Leads Lift"
                  value={option.value}
                  label={option.label}
                  toggled={optionValue === option.value}
                  disabled={isLLOptionEnabled}
                  handleOnChange={handleOnChange}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enable/disable div based on radio selection */}
      <div
        className="mx-auto max-w-lg space-y-3 p-1 -mt-1"
        style={!isLLOptionEnabled ? disableStyle : {}}
      >
        <h4 className="text-base font-semibold">
          {minAmountFilter != null ? 'Change Tier' : 'Search Tier'}
        </h4>

        <SearchDropdown
          options={dropdownOptions}
          onSelect={opt => {
            const text =
              typeof opt === 'string' ? opt : opt?.label ?? opt?.value ?? ''
            const amt = parseAmount(text)
            if (!Number.isFinite(amt)) return

            setPicked({
              label: text,
              value: text,
              amountNum: amt,
              amountStr: `$${amt.toLocaleString()}`,
              tierLabel: text.split('-')[0].trim(),
            })
            setLLAmount?.(amt)
          }}
          placeholder={
            minAmountFilter != null
              ? `Current: $${Number(
                  minAmountFilter,
                ).toLocaleString()} — search to change`
              : 'Search Tier…'
          }
        />

        {picked && (
          <div className="rounded-none border border-gray-200 p-3 text-sm text-gray-700">
            <strong>Selected:</strong>{' '}
            {picked.amountNum != null
              ? picked.amountNum.toLocaleString()
              : picked.amountStr}
          </div>
        )}
        <p className="text-xs text-gray-500">
          Tip: try typing “st”, “ex”, “50”, etc.
        </p>
      </div>
    </div>
  )
}

export {LeadsLiftV4}
